import React, { forwardRef, useEffect, useMemo } from 'react';
import { CircularProgress, FormControl, FormHelperText, InputLabel, SxProps, Theme } from '@mui/material';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import useGroups from 'views/App/Portal/LexID/Groups/_data_sources/useGroups';
import { Group } from 'views/App/Portal/LexID/Groups/_types/group';

interface GroupOption {
  value: string;
  label: string;
  group: Group;
}

export type GroupPickerProps = {
  allowBlank?: boolean;
  allowMultiple?: boolean;
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  organizationId: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
  sx?: SxProps<Theme>;
};

const GroupPicker = forwardRef(
  (
    {
      allowBlank = false,
      allowMultiple = false,
      id,
      label,
      value = [],
      onChange,
      organizationId,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
      sx,
    }: GroupPickerProps,
    ref
  ) => {
    const groupsQuery = useGroups(organizationId, { size: 100 });

    const groupOptions = useMemo(() => {
      try {
        const allGroups = groupsQuery.data?.pages?.flatMap((page) => page.content) ?? [];
        return [
          ...(allowBlank ? [{ value: '', label: placeholder ?? '-- All Groups --' }] : []),
          ...allGroups.map((x) => ({ value: x.id!, label: x.name ?? '', group: x })),
        ];
      } catch (e) {
        return [];
      }
    }, [groupsQuery.data, allowBlank]);

    const selectedOptions = useMemo(() => {
      return groupOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, groupOptions]);

    useEffect(() => {
      if (groupsQuery.hasNextPage) {
        groupsQuery.fetchNextPage();
      }
    }, [groupsQuery.data, groupsQuery.hasNextPage]);

    return groupsQuery.data?.pages[0]?.content ? (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={sx}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={groupOptions}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isMulti={allowMultiple}
          isSearchable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              const newVals = newValue as MultiValue<GroupOption>;
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              const newVal = newValue as SingleValue<GroupOption>;
              onChange(!!newVal ? [newVal.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    ) : (
      <CircularProgress />
    );
  }
);

export default GroupPicker;
